import { InputAdornment } from '@mui/material'
import { Card, Input } from '@nextui-org/react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import MainButton from '../common/util/input/MainButton'
import { floatRegEx } from '../editor/EditorComponents'
import { getBoostAction } from '../helpers/WaxApi'
import { PopupLoadingIndicator } from '../loadingindicator/PopupLoadingIndicator'
import Popup from './Popup'
import { useSharedState } from '../waxplorer/Waxplorer'
import { formatUSD, formatWAX } from '../helpers/FormatLinks'

function EditPopup(props) {
    const asset = props['asset']
    const listing = props['listing']

    let { schema, assetId, image, name, template } = asset

    const { stats } = template ? template : { stats: null }

    let { collection, assets, price, currency } = listing

    const { t } = useTranslation('common')

    const [state] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    const callBack = props['callBack']
    const [isLoading, setIsLoading] = useState(false)
    const closeCallBack = props['closeCallBack']
    const [sellPrice, setSellPrice] = useState(currency === 'WAX' ? price : 0)

    const cancelSale = async () => {
        setIsLoading(true)

        let { market, listingId, seller } = listing

        const actions = [getBoostAction(activeUser)]

        if (market === 'atomicmarket') {
            actions.push({
                account: market,
                name: 'cancelsale',
                authorization: [
                    {
                        actor: userName,
                        permission: activeUser.requestPermission
                            ? activeUser.requestPermission
                            : 'active',
                    },
                ],
                data: {
                    sale_id: listingId,
                },
            })
        }

        try {
            const result = await activeUser.session.transact(
                {
                    actions: actions,
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )

            callBack({
                success: true,
                listing: null,
                canceled: true,
                edited: false,
                error: null,
            })
        } catch (e) {
            console.error(e)
            callBack({
                success: false,
                edited: false,
                canceled: false,
                error: e.message,
            })
        }

        setIsLoading(false)
        closeCallBack()
    }

    const getListingId = (result) => {
        if (result?.response?.processed?.action_traces) {
            for (const action of result.response.processed.action_traces) {
                if (action.act.name === 'lognewsale') {
                    return action.act.data.sale_id
                }
            }
        }

        return null
    }

    const editSale = async () => {
        setIsLoading(true)

        let { listingId, assets } = listing
        const actions = [getBoostAction(activeUser)]

        const assetIds = []

        for (const asset of assets) {
            assetIds.push(asset['assetId'])
        }

        actions.push({
            account: 'atomicmarket',
            name: 'cancelsale',
            authorization: [
                {
                    actor: userName,
                    permission: activeUser.requestPermission
                        ? activeUser.requestPermission
                        : 'active',
                },
            ],
            data: {
                sale_id: listingId,
            },
        })

        actions.push({
            account: 'atomicmarket',
            name: 'announcesale',
            authorization: [
                {
                    actor: userName,
                    permission: activeUser.requestPermission
                        ? activeUser.requestPermission
                        : 'active',
                },
            ],
            data: {
                seller: userName,
                maker_marketplace:
                    process.env.NEXT_PUBLIC_TESTNET === 'TRUE'
                        ? 'nfthiveboost'
                        : 'nft.hive',
                settlement_symbol: '8,WAX',
                asset_ids: assetIds,
                listing_price: parseFloat(sellPrice).toFixed(8) + ' WAX',
            },
        })

        actions.push({
            account: 'atomicassets',
            name: 'createoffer',
            authorization: [
                {
                    actor: userName,
                    permission: activeUser.requestPermission
                        ? activeUser.requestPermission
                        : 'active',
                },
            ],
            data: {
                sender: userName,
                recipient: 'atomicmarket',
                sender_asset_ids: assetIds,
                recipient_asset_ids: [],
                memo: 'sale',
            },
        })
        try {
            const result = await activeUser.session.transact(
                {
                    actions: actions,
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )

            const newListingId = getListingId(result)
            callBack({
                success: true,
                edited: true,
                canceled: false,
                listing: {
                    price: sellPrice,
                    currency: 'WAX',
                    listingId: newListingId,
                },
                transactionId: result?.response?.transaction_id,
            })
        } catch (e) {
            console.error(e)
            callBack({
                success: false,
                edited: false,
                canceled: false,
                error: e.message,
            })
        }

        setIsLoading(false)
        closeCallBack()
    }

    const changePrice = (e) => {
        const val = e.target.value
        if (/^\d*\.?\d*$/.test(val)) setSellPrice(val)
    }

    const cancel = () => {
        callBack({
            success: false,
            edited: false,
            canceled: false,
            error: null,
        })
        closeCallBack()
    }

    return (
        <Popup title={'Edit - ' + name} cancel={cancel} image={image}>
            <div className="p-4 ">
                <Card className="p-2 mt-2 mb-4">
                    <div className="grid grid-cols-2">
                        <div className="col-span-1 ">
                            <div className="flex flex-col gap-2 ">
                                <b>{t('asset.schema')}:</b>
                                <b>{t('asset.collection')}:</b>
                                <b>{t('asset.lowest_available_price')}:</b>
                                <b>{t('asset.average_price')}:</b>
                                <b>{t('asset.last_sold')}:</b>
                            </div>
                        </div>
                        <div className="col-span-1">
                            <div className="flex flex-col items-end justify-end gap-2 ">
                                <div>{schema}</div>
                                <div>{collection.collectionName}</div>
                                <div>
                                    {stats
                                        ? formatWAX(stats['floorPrice'])
                                        : ''}
                                </div>
                                <div>
                                    {stats && stats.averageWaxPrice
                                        ? formatWAX(stats['averageWaxPrice'])
                                        : ''}
                                    {stats && stats.averageUsdPrice ? (
                                        <span className="usd">
                                            /
                                            {formatUSD(
                                                stats['averageUsdPrice'],
                                            )}
                                        </span>
                                    ) : (
                                        ''
                                    )}
                                </div>
                                <div>
                                    {stats && stats.lastSold
                                        ? formatWAX(
                                              stats['lastSold']['priceWax'],
                                          )
                                        : ''}
                                    {stats && stats.lastSold ? (
                                        <span className="usd">
                                            /
                                            {formatUSD(
                                                stats['lastSold']['priceUsd'],
                                            )}
                                        </span>
                                    ) : (
                                        ''
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>

                <div className={'flex m-auto'}>
                    <Input
                        variant="faded"
                        endContent={'WAX'}
                        value={sellPrice ? sellPrice : ''}
                        placeholder={t('asset.new_price')}
                        inputProps={{
                            style: {
                                height: '1.2rem',
                                fontSize:
                                    String(sellPrice ? sellPrice : '').length >
                                    8
                                        ? '0.6rem'
                                        : String(sellPrice ? sellPrice : '')
                                              .length > 5
                                        ? '0.7rem'
                                        : '1rem',
                            },
                        }}
                        onChange={changePrice}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment
                                    position="end"
                                    disableTypography
                                    sx={{
                                        fontSize: '0.7rem',
                                        marginLeft: 0.2,
                                    }}
                                >
                                    WAX
                                </InputAdornment>
                            ),
                        }}
                        error={!floatRegEx.test(sellPrice)}
                    />
                </div>
                <div className="flex justify-between gap-2 mt-4">
                    <MainButton
                        disabled={!sellPrice && 'disabled'}
                        onClick={editSale}
                    >
                        {'Set Price'}
                    </MainButton>
                    <MainButton className="bg-red-500" onClick={cancelSale}>
                        {'Cancel Listing'}
                    </MainButton>
                </div>
                {isLoading ? (
                    <PopupLoadingIndicator
                        text={'Loading Transactions'}
                        isLoading={isLoading}
                    />
                ) : (
                    ''
                )}
            </div>
        </Popup>
    )
}

export default EditPopup
